import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { AppSettings } from 'projects/sso/src/app.settings';
import { PasswordChangeRequest, PasswordChangeResponse } from '../model/password-change.model';
import { AuthService } from 'projects/sso/src/app/core/service/auth.service';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { MfaTokenPayload } from '../../../mfa/model/mfa.model';
import {
  AppPageService,
  AuthThrottleService,
  InitMfaData,
  MessageService,
  MfaApiResponse,
  MfaConfig,
  MfaContextType,
  MfaErrorCodeType,
  MfaErrorType,
  MfaService
} from 'common';

@Injectable({
  providedIn: 'root'
})
export class PasswordChangeService {

  constructor(
    private http: HttpClient,
    private settings: AppSettings,
    private authService: AuthService,
    private messageService: MessageService,
    private mfaService: MfaService,
    private appPageService: AppPageService,
    private readonly authThrottleService: AuthThrottleService
  ) { }

  submit(req: PasswordChangeRequest): Observable<unknown> {
    if (req.ssoToken) {
      return this.changePassword(req);
    } else {
      return this.authService.ssoToken$.pipe(
        switchMap((ssoToken) => this.changePassword({ ...req, ssoToken: ssoToken.ssoToken }))
      );
    }
  }

  changePassword(req: PasswordChangeRequest, token?: MfaTokenPayload): Observable<string | PasswordChangeResponse> {
    let headers = {};
    headers = {
      'Content-Type': 'application/x-www-form-urlencoded'
    };
    if (token?.mfaToken) {
      headers = { ...headers, 'X-Ryno-Mfa-Token': token?.mfaToken };
    }
    if (token?.mfaCode) {
      headers = { ...headers, 'X-Ryno-Mfa-Code': token.mfaCode };
    }
    const body: string = this.authService.transformToWwwForm(req);

    return this.http.post<string>(`${this.settings.sso.url}/v2/change-password`, body, { headers: headers })
      .pipe(catchError((error) => this.submitError(error, req)),
        tap((data: PasswordChangeResponse) => this.handleResponse(data, req.returnUrl)));
  }

  private handleResponse(data: PasswordChangeResponse, returnUrl: string): void {
    if (!data?.hasError) {
      this.messageService.success(`Your password has been changed.${returnUrl ? ' You will be automatically redirected in 5 seconds.' : ''}`)
      this.appPageService.back();
      if (returnUrl) {
        timer(5000).subscribe(() => {
          window.location.href = returnUrl;
        });
      }
    }
  }

  private submitError(response: HttpErrorResponse, req: PasswordChangeRequest): Observable<PasswordChangeResponse> {
    const throttle = this.authThrottleService.handle(response);
    if (throttle) {
      return of({ hasError: true, errorCode: throttle });
    }

    let message = '';

    switch (response.status) {
      case 400: {
        message = response.error.title ?? response.error[0].description;
        break;
      }
      case 401: {
        message = 'Session expired. Please refresh the page and try again.';
        break;
      }
      case 403:
        if (this.hasMfaError(response?.error?.error)) {
          return this.handleMfaErrors(response?.error, req);
        }
        message = response.error
          || 'Error ocurred while trying to change password';
        break;
      default: {
        message = response.error
          || 'Error ocurred while trying to change password';
        break;
      }
    }
    this.messageService.error(message);
    return of();
  }

  private handleMfaErrors(mfaResponse: MfaApiResponse, req: PasswordChangeRequest): Observable<{ hasError: boolean; }> {
    switch (mfaResponse.error) {
      case MfaErrorType.MfaRequired:
        return this.handleMfaRequiredError(mfaResponse, req);
      case MfaErrorType.MfaCodeError:
        return this.handleMfaCodeError(mfaResponse);
      default:
        console.error(`Mfa error: ${mfaResponse.error}`);
        return of({ hasError: true });
    }
  }

  private handleMfaRequiredError(
    mfaResponse: MfaApiResponse,
    req: PasswordChangeRequest
  ): Observable<{ hasError: boolean, errorCode?: MfaErrorCodeType }> {

    const config: MfaConfig = {
      canAddPhoneNumberIfMissing: false,
      canChangePhoneNumberIfUnverified: false,
      isTrustingDeviceAllowed: false,
      isBackButtonVisible: false
    };

    const data: InitMfaData = {
      mfaContextType: MfaContextType.ChangePassword,
      mfaResponse,
      config,
      contextData: { ...req }
    };

    this.mfaService.initMfa(data);
    return of({ hasError: true });
  }

  private handleMfaCodeError(mfaResponse: MfaApiResponse): Observable<{
    hasError: boolean,
    errorCode: MfaErrorCodeType
  }> {
    if (
      mfaResponse?.send_code_status?.error_result?.error ===
      MfaErrorCodeType.InvalidMfaCode
    ) {
      return of({ hasError: true, errorCode: MfaErrorCodeType.InvalidMfaCode });
    }
  }

  private hasMfaError(errorType: MfaErrorType): boolean {
    return Object.values(MfaErrorType).includes(errorType);
  }
}
